"use client";

import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";

import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "../lib/utils";

interface DatePickerProps {
  date?: Date;
  setDate: (date: Date | null) => void;
  placeholder?: string;
  onDateSelect?: (date: Date | undefined) => void;
  withConfirmButton?: boolean;
}

export function DatePicker({
  date,
  setDate,
  placeholder = "Pick a date",
  onDateSelect,
  withConfirmButton = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [tempDate, setTempDate] = React.useState<Date | undefined>(date);

  // Update tempDate when date prop changes
  React.useEffect(() => {
    setTempDate(date);
  }, [date]);

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (withConfirmButton) {
      // Just store the date temporarily when using confirm button
      setTempDate(selectedDate);
    } else {
      // Auto-close behavior
      if (selectedDate) {
        setDate(selectedDate);
        onDateSelect?.(selectedDate);
        setOpen(false);
      } else {
        setDate(null);
        onDateSelect?.(undefined);
      }
    }
  };

  const handleConfirm = () => {
    if (tempDate) {
      setDate(tempDate);
      onDateSelect?.(tempDate);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={withConfirmButton ? tempDate : date}
          onSelect={handleDateSelect}
          initialFocus
        />
        {withConfirmButton && (
          <div className="flex justify-end p-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleConfirm}
              disabled={!tempDate}
            >
              Confirmar
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
